import React, { Suspense } from 'react'
import { Route, Routes } from 'react-router'
import { ROUTES } from '@constants/index'
import { config } from '@config/config'
import { useEnvironment } from '@hooks/useEnvironment'
import { Error } from '@app/pages/error/Error'
import { InsuranceFormRedirect } from '@app/pages/redirect/InsuranceFormRedirect'
import { InsuranceMemberRedirect } from '@app/pages/redirect/InsuranceMemberRedirect'
import { FallbackIndicator } from './FallbackIndicator'

const Intro = React.lazy(() => import('@app/pages/intro/Intro'))
const EligibleInsurers = React.lazy(() => import('@app/pages/insurers/Insurers'))
const MultiStepInsuranceInformation = React.lazy(() => import('@app/pages/insurance-information/InsuranceInformation'))
const SingleStepInsuranceInformation = React.lazy(
  () => import('@app/pages/single-step-insurance-information/SingleStepInsuranceInformation')
)
const SingleStepIntro = React.lazy(() => import('@app/pages/single-step-intro/SingleStepIntro'))
const Quote = React.lazy(() => import('@app/pages/quote/Quote'))
const QuoteAccepted = React.lazy(() => import('@app/pages/quote-accepted/QuoteAccepted'))
const SelectPrescription = React.lazy(() => import('@app/pages/select-prescription/SelectPrescription'))
const ValidatePrescription = React.lazy(() => import('@app/pages/validate-prescription/ValidatePrescription'))
const SelectProducts = React.lazy(() => import('@app/pages/select-product/SelectProduct'))
const ReviewClaim = React.lazy(() => import('@app/pages/review-claim/ReviewClaim'))
const SubmitClaim = React.lazy(() => import('@app/pages/submit-claim/SubmitClaim'))
const ClaimAccepted = React.lazy(() => import('@app/pages/claim-accepted/ClaimAccepted'))
const Benefits = React.lazy(() => import('@app/pages/benefits/Benefits'))
const Loading = React.lazy(() => import('@app/pages/loading/Loading'))
const FindMember = React.lazy(() => import('@app/pages/find-member/FindMember'))
const ApplyInsurance = React.lazy(() => import('@app/pages/apply-insurance/ApplyInsurance'))
const DistributeBenefits = React.lazy(() => import('@app/pages/distribute-benefits/DistributeBenefits'))
const Restricted = React.lazy(() => import('@app/pages/restricted/Restricted'))

export const AppRoutes: React.FC = () => {
  const { basePath } = useEnvironment()
  const dbpath = basePath.endsWith('/') ? ROUTES.BASE_NO_SLASH : ROUTES.BASE

  // available routes are based on the insurance flow type for the brand
  const flowType = config.getFeatures().insuranceFlowType

  // optional for some US brands
  const disableDistributeBenefits = config.getFeatures().disableDistributeBenefits

  return (
    <Suspense fallback={<FallbackIndicator />}>
      <Routes>
        <Route path={`${basePath}${dbpath}`}>
          {/* Common to all flows */}
          <Route path={ROUTES.ERROR} element={<Error />} />
          <Route path={ROUTES.LOADING} element={<Loading />} />

          {flowType === 'CA_FLOW' && (
            <>
              <Route index element={<Intro />} />
              <Route path={ROUTES.START} element={<InsuranceFormRedirect />} />
              <Route path={ROUTES.ELIGIBLE_INSURERS} element={<EligibleInsurers />} />
              <Route path={ROUTES.INSURANCE_INFORMATION} element={<MultiStepInsuranceInformation />} />
              <Route path={ROUTES.SELECT_PRESCRIPTION} element={<SelectPrescription />} />
              <Route path={ROUTES.VALIDATE_PRESCRIPTION} element={<ValidatePrescription />} />
              <Route path={ROUTES.SELECT_PRODUCT} element={<SelectProducts />} />
              <Route path={ROUTES.REVIEW_CLAIM} element={<ReviewClaim />} />
              <Route path={ROUTES.SUBMIT_CLAIM} element={<SubmitClaim />} />
              <Route path={ROUTES.ACCEPTED} element={<ClaimAccepted />} />
            </>
          )}

          {flowType === 'VD_FLOW' && (
            <>
              <Route index element={<SingleStepIntro />} />
              <Route path={ROUTES.START} element={<InsuranceFormRedirect />} />
              <Route path={ROUTES.INSURANCE_INFORMATION} element={<SingleStepInsuranceInformation />} />
              <Route path={ROUTES.QUOTE} element={<Quote />} />
              <Route path={ROUTES.ACCEPTED} element={<QuoteAccepted />} />
            </>
          )}

          {flowType === 'US_FLOW' && (
            <>
              <Route path={ROUTES.START} element={<InsuranceMemberRedirect />} />
              <Route path={ROUTES.APPLY_INSURANCE} element={<ApplyInsurance />} />
              <Route path={ROUTES.FIND_MEMBER} element={<FindMember />} />
              <Route path={ROUTES.BENEFITS} element={<Benefits />} />
              <Route path={ROUTES.RESTRICTED} element={<Restricted />} />

              {!disableDistributeBenefits && (
                <Route path={ROUTES.DISTRIBUTE_BENEFITS} element={<DistributeBenefits />} />
              )}
            </>
          )}
        </Route>
      </Routes>
    </Suspense>
  )
}
